import { ChangeDetectorRef, Component } from '@angular/core';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationExtras,
  Router,
  RouterLink,
} from '@angular/router';
import { CommonModule } from '@angular/common';
import { filter } from 'rxjs';
import {
  BREADCRUMB_SECTIONS,
  COMPLIANCE_MAP,
  CONTROL_TASK_MAP,
  DIGITAL_ASSET_MAP,
  EXTERNAL_SURFACE_MAP,
  LIBRARY_MAP,
  ONBOARDING_MAP,
  PRIVACY_MAP,
  RISK_IMPACT_MAP,
  SCC_MAP,
  SECURITY_INTEGRATIONS_MAP,
  VULNERABILITY_MAP,
  WORK_MAP,
  WORKSPACE_MAP,
} from '../../helpers/constants/breadcrumb.constant';
import { FORM_ID } from '../../helpers/form-id.enum';

@Component({
  selector: 'app-breadcrumb',
  imports: [CommonModule],
  templateUrl: './breadcrumb.component.html',
  styleUrl: './breadcrumb.component.css',
})
export class BreadcrumbComponent {
  urlSegments: any;
  breadcrumbItems: any[] = [];
  taskId: any;
  actionableId: any;
  domain: any;
  actionId: any;
  constructor(
    private cdRef: ChangeDetectorRef,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  ngOnInit(): void {
    this.getParams();
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.updateBreadcrumb();
      });
    this.updateBreadcrumb();
  }

  getParams() {
    this.route.paramMap.subscribe((params) => {
      this.taskId = params.get('taskId') || '';
      this.actionableId = params.get('actionId') || '';
    });
  }

  updateBreadcrumb() {
    // Remove query params (everything after '?')
    const cleanUrl = this.router.url.split('?')[0];

    // Known app prefixes that should never appear in breadcrumb labels
    const hiddenPrefixes = [
      'eciso',
      'scc',
      'dmarc',
      'easm',
      'emradar',
      'edai',
      'erisk',
      'escr',
      'ecompliance',
      'settings',
    ];

    const urlSegments = cleanUrl.split('/').filter((segment) => segment);
    this.urlSegments = urlSegments.filter((segment, index, arr) => {
      // Keep JWT tokens out always
      if (this.isJwtToken(segment)) return false;

      // ✅ Keep IDs if they come right after 'onboarding', 'framework-details', or 'asset-details'
      const prevSegment = arr[index - 1];
      if (
        prevSegment === 'onboarding' ||
        prevSegment === 'framework-details' ||
        prevSegment === 'asset-details'
      )
        return true;

      // Remove other random IDs
      return !this.isId(segment);
    });

    this.generateBreadcrumb();
    this.cdRef.detectChanges();
  }

  isJwtToken(segment: string): boolean {
    return (
      segment.includes('.') &&
      segment.split('.').length === 3 &&
      segment.length > 50
    );
  }

  generateBreadcrumb() {
    const items: any[] = [];
    let url = '';

    let segments = this.urlSegments || [];

    // Hide the app prefix from the breadcrumb text, but use it to build URLs
    const knownPrefixes = [
      'eciso',
      'scc',
      'dmarc',
      'easm',
      'emradar',
      'edai',
      'erisk',
      'escr',
      'ecompliance',
      'settings',
    ];
    let appPrefix = '';
    if (segments.length > 0 && knownPrefixes.includes(segments[0])) {
      appPrefix = '/' + segments[0];
      url = appPrefix;
      segments = segments.slice(1);
    }

    if (appPrefix === '/settings' && segments[0] === 'general') {
      this.breadcrumbItems = [
        { label: 'General', url: '' },
        { label: 'General Settings', url: '/settings/general' },
      ];
      return;
    }

    // Email settings form is opened through the onboarding route
    if (
      segments[0] === 'onboarding' &&
      segments[1] === FORM_ID.NOTIFICATIONS_INTEGRATIONS
    ) {
      this.breadcrumbItems = [
        { label: 'General', url: '' },
        {
          label: 'Email Settings',
          url: `${appPrefix}/onboarding/${segments[1]}${segments[2] ? '/' + segments[2] : ''}`,
        },
      ];
      return;
    }

    // ------------------------------
    // 🔥 SPECIAL CASE: /asset-details or /assets-list
    // ------------------------------
    if (
      segments[0] === 'asset-details' ||
      segments[0] === 'assets-list'
    ) {
      const isDetailsFlow = segments[0] === 'asset-details';
      const assetId = segments[1] || '';

      const section = BREADCRUMB_SECTIONS.find(
        (sec: any) => sec.parent === 'Control & Task Operations',
      );

      // Level 0: Control & Task Operations
      items.push({
        label: section?.parent || 'Control & Task Operations',
        url: section?.baseUrl || `${appPrefix}/task-dashboard`,
      });

      // Level 1: Assets (clicking navigates to /eciso/assets-list)
      items.push({
        label: 'Assets',
        url: `${appPrefix}/assets-list`,
      });

      // Level 2: Asset ID
      if (isDetailsFlow && assetId) {
        items.push({
          label: assetId,
          url: `${appPrefix}/asset-details/${assetId}`,
        });
      }

      this.breadcrumbItems = items;
      return;
    }

    // ------------------------------
    // 🔥 SPECIAL CASE: /framework-details or /framework-library
    // ------------------------------
    if (
      segments[0] === 'framework-details' ||
      segments[0] === 'framework-library'
    ) {
      const isDetailsFlow = segments[0] === 'framework-details';
      const frameworkId = segments[1] || '';

      const section = BREADCRUMB_SECTIONS.find(
        (sec: any) => sec.parent === 'Library',
      );

      // Level 0: Library
      items.push({
        label: section?.parent || 'Library',
        url: section?.baseUrl || `${appPrefix}/compliance-dashboard`,
      });

      // Level 1: Framework Library
      items.push({
        label: 'Framework Library',
        url: `${appPrefix}/framework-library`,
      });

      // Level 2: Selected Framework ID / Name
      if (isDetailsFlow && frameworkId) {
        let frameworkLabel = frameworkId;
        try {
          const storedName = sessionStorage.getItem('selectedFrameworkName');
          if (storedName) {
            frameworkLabel = storedName;
          }
        } catch {}

        items.push({
          label: frameworkLabel,
          url: `${appPrefix}/framework-details/${frameworkId}`,
        });
      }

      this.breadcrumbItems = items;
      return;
    }

    // ------------------------------
    // 🔥 SPECIAL CASE: /manual-risk (list only)
    // ------------------------------
    if (segments[0] === 'manual-risk' && segments.length === 1) {
      const section = BREADCRUMB_SECTIONS.find(
        (sec: any) => sec.parent === 'Risk Register',
      );

      if (section) {
        items.push({ label: section.parent, url: section.baseUrl });
        items.push({ label: 'Manual Risks', url: `${appPrefix}/manual-risk` });
      }

      this.breadcrumbItems = items;
      return;
    }

    // ------------------------------
    // 🔥 SPECIAL CASE: /risk-register/:riskId or /manual-risk/:riskId
    // ------------------------------
    if (
      (segments[0] === 'risk-register' || segments[0] === 'manual-risk' || segments[0] === 'risk-details') &&
      segments.length > 1
    ) {
      const riskId = segments[1];
      const isManualRiskFlow = segments[0] === 'manual-risk';

      const section = BREADCRUMB_SECTIONS.find(
        (sec: any) => sec.parent === 'Risk & Impact Management',
      );

      if (section) {
        // ✅ Parent
        items.push({
          label: section.parent,
          url: section.baseUrl,
        });

        // ✅ Level 1 list
        if (isManualRiskFlow) {
          items.push({
            label: 'Manual Risks',
            url: `${appPrefix}/manual-risk`,
          });
        } else {
          items.push({
            label: 'System Risks',
            url: `${appPrefix}/system-risk`,
          });
        }

        // ✅ Risk ID
        if (riskId) {
          items.push({
            label: riskId,
            url: `${appPrefix}/${segments[0]}/${riskId}`,
          });
        }
      }

      this.breadcrumbItems = items;
      return;
    }

    // ------------------------------
    // 🔥 SPECIAL CASE: /tasks or /my-tasks (FULL HANDLING)
    // ------------------------------
    if (segments[0] === 'tasks' || segments[0] === 'my-tasks') {
      let navCtx: any = {};
      try {
        navCtx = JSON.parse(sessionStorage.getItem('taskViewNav') || '{}');
      } catch {}

      // If we are on /eciso/my-tasks, or we are on /eciso/tasks/:taskId but we navigated from my-tasks
      const isMyTasksFlow =
        segments[0] === 'my-tasks' || navCtx?.source === 'my-tasks';
      const isActionablesFlow = navCtx?.source === 'actionables';
      const isAllActionablesFlow = navCtx?.source === 'all-actionables';

      let parentLabel = 'Control & Task Operations';
      let parentUrl = `${appPrefix}/task-dashboard`;
      let level1Label = 'All Tasks';
      let level1Url = `${appPrefix}/tasks`;

      if (isMyTasksFlow) {
        parentLabel = 'My Workspace';
        parentUrl = `${appPrefix}/dashboard`;
        level1Label = 'My Tasks';
        level1Url = `${appPrefix}/my-tasks`;
      } else if (isActionablesFlow) {
        parentLabel = 'My Workspace';
        parentUrl = `${appPrefix}/dashboard`;
        level1Label = 'My Action Items';
        level1Url = `${appPrefix}/my-action-items`;
      } else if (isAllActionablesFlow) {
        parentLabel = 'Control & Task Operations';
        parentUrl = `${appPrefix}/task-dashboard`;
        level1Label = 'All Action Items';
        level1Url = `${appPrefix}/all-action-items`;
      }

      // ✅ Parent
      items.push({
        label: parentLabel,
        url: parentUrl,
      });

      // ✅ Level 1 list (My Tasks, All Tasks, or My Action Items)
      items.push({
        label: level1Label,
        url: level1Url,
      });

      // ✅ Task ID (Level 2)
      if (segments.length > 1 && segments[0] === 'tasks') {
        const taskId = segments[1];

        items.push({
          label: taskId,
          url: `${appPrefix}/tasks/${taskId}`,
        });

        // ✅ Actionables (Level 3)
        if (segments[2] === 'actionables' && segments[3]) {
          const actionableId = segments[3];

          items.push({
            label: actionableId,
            url: `${appPrefix}/tasks/${taskId}/actionables/${actionableId}`,
          });
        }
      }

      this.breadcrumbItems = items;
      return;
    }
    // ------------------------------
    // ✅ MAIN LOOP
    // ------------------------------
    for (const segment of segments) {
      if (!segment) continue;

      url += `/${segment}`;

      // ------------------------------
      // ✅ SECTION MATCH (GENERIC)
      // ------------------------------
      const section = BREADCRUMB_SECTIONS.find(
        (sec: any) => sec.children[segment],
      );

      if (section) {
        // Add parent only once
        if (!items.find((i: any) => i.label === section.parent)) {
          items.push({
            label: section.parent,
            url: section.baseUrl,
          });
        }

        let childLabel =
          section.children[segment as keyof typeof section.children];

        if (
          segment === 'organization-setting' &&
          this.router.url.includes('tab=employee')
        ) {
          childLabel = 'Employees';
        }

        if (childLabel && childLabel !== section.parent) {
          items.push({
            label: childLabel,
            url,
          });
        }

        continue;
      }

      // ------------------------------
      // EXISTING LOGIC
      // ------------------------------
      if (ONBOARDING_MAP[segment]) {
        items.push({ label: ONBOARDING_MAP[segment], url });
      } else if (LIBRARY_MAP[segment]) {
        if (!items.find((i) => i.label === 'Library')) {
          items.push({ label: 'Library', url: '/library' });
        }
        items.push({ label: LIBRARY_MAP[segment], url });
      }
      //  else if (WORK_MAP[segment]) {
      //   if (
      //     (segment === 'tasks' && hasTaskAssignedParam) ||
      //     (segment === 'actionables-list' &&
      //       !items.find((i) => i.label === 'My Work'))
      //   ) {
      //     items.push({ label: 'My Work', url: '/my-work' });
      //   }

      //   items.push({ label: WORK_MAP[segment], url });
      // }
      else {
        const label = this.getLabel(segment);
        if (label && !/^\d+$/.test(label)) {
          items.push({ label, url });
        }
      }
    }

    this.breadcrumbItems = items;
  }

  getLabel(
    segment: string,
    context?: { endId?: string; actionableId?: string },
  ): string {
    if (segment === 'executive-dashboard') {
      return 'Executive Dashboard';
    }
    if (segment.startsWith('dashboard')) {
      return 'Dashboard';
    }
    switch (segment) {
      case 'admin':
        return '';
      case 'dashboard':
        return 'Dashboard';
      case 'employee':
        return 'Employee';
      case 'location':
        return 'Location';
      // case 'tasks':
      //   return 'Tasks';
      case 'tasks':
        return `Tasks `;
      case 'actionables':
        return `Tasks`;
      case 'compliances':
        return 'My Compliance';
      case 'documents':
        return 'Document Library';
      case 'integration':
        return 'Integrations';
      case 'evidences':
        return 'Evidence Library';
      case 'assets':
        return 'Assets Library';
      case 'security-landscape':
        return 'Security Landscape';
      case 'organization-settings':
        return 'Organization Settings';
      case 'onboarding':
        return 'Configurations';
      case 'bookmarks':
        return 'Bookmarks';
      case 'utility-tools':
        return 'Utility Tools';
      case 'assets-library':
        return 'Assets Library';
      case 'policy-library':
        return 'Policy Library';
      case 'evidence-library':
        return 'Evidence Library';
      case 'ssl-library':
        return 'SSL Library';
      case 'domain-library':
        return 'Domain Library';
      case 'activity-logs':
        return 'Activity Logs';
      case 'my-assets':
        return 'My Assets';
      case 'assets-list':
        return 'Assets';
      case 'organization-setting':
        return 'Organization Settings';
      case 'my-profile':
        return 'My Profile';
      case 'manual-risk':
        return 'Manual Risk';
      case 'all-action-items':
        return 'All Action Items';
      case 'integration-marketplace':
        return 'Integration Marketplace';
      case 'notification-preferences':
        return 'Notification Preferences';
      default:
        return segment.charAt(0).toUpperCase() + segment.slice(1);
    }
  }

  isId(segment: string): boolean {
    return /^[0-9a-fA-F]{24}$/.test(segment);
  }

  onBreadcrumbClick(item: any): void {
    const currentUrl = this.router.url;
    const path = currentUrl.split('?')[0];
    const segments = path.split('/').filter(Boolean);

    // URL: /tasks/:taskId/actionables/:actionableId/:recordId
    const taskId = segments[1] || '';
    const actionableId = segments[3] || '';

    // Preserve query params
    const queryParams: any = {};
    const searchParams = new URLSearchParams(currentUrl.split('?')[1] || '');
    searchParams.forEach((value, key) => {
      queryParams[key] = value;
    });

    let url: any[] = [];

    // -------------------------
    // Breadcrumb routing
    // -------------------------

    // ✅ Tasks
    if (item.type === 'tasks' || item.label === 'Tasks') {
      url = ['/eciso/tasks'];
    }

    // ✅ Assets
    else if (item.label === 'Assets') {
      url = ['/eciso/assets-list'];
    }

    // ✅ Task view
    else if (item.type === 'task-view') {
      url = ['/eciso/tasks', taskId];
    }

    // ✅ Actionable list (virtual level)
    else if (item.type === 'actionables') {
      url = ['/eciso/tasks', taskId, 'actionables'];
    }

    // ✅ Actionable view
    else if (item.type === 'actionable-view') {
      url = ['/eciso/tasks', taskId, 'actionables', actionableId];
    }

    // ✅ Fallback
    else if (item.url) {
      url = [item.url];
    }

    // -------------------------
    // Navigate
    // -------------------------
    if (url.length) {
      const isMyActionItems = url.some(
        (u) => typeof u === 'string' && u.includes('my-action-items'),
      );
      if (isMyActionItems) {
        if (queryParams['actionableSearch']) {
          queryParams['search'] = queryParams['actionableSearch'];
          delete queryParams['actionableSearch'];
        }
        delete queryParams['assigned'];
        this.router.navigate(url, {
          queryParams,
        });
      } else {
        this.router.navigate(url, {
          queryParams,
          queryParamsHandling: 'preserve',
        });
      }
    } else {
      console.warn('No valid URL found for breadcrumb item:', item);
    }
  }

  navigateToDashboard() {
    // this.router.navigate([ '/executive-dashboard']);
    this.router.navigate(['/eciso/executive-dashboard']);
  }

  isParentLabel(label: string): boolean {
    return (
      label === 'General' ||
      BREADCRUMB_SECTIONS.some((section) => section.parent === label)
    );
  }
}
